import { PlusOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { Handle, Position } from "reactflow";
import { useWorkflowStore } from "../store";

export interface AddStepNodeData {
    insertIndex: number;
    parentStepId?: string;
    isEnabled?: boolean;
}

const AddStepNodeComponent: React.FC<{ data: AddStepNodeData }> = ({ data }) => {
    const [open, setOpen] = useState(false);
    const addStep = useWorkflowStore((s) => s.addStep);
    const closeSignal = useWorkflowStore((s) => s.closeSignal);
    const isEnabled = data.isEnabled ?? true;

    useEffect(() => {
        if (open) setOpen(false);
    }, [closeSignal]); // close popover when editor signals movement/pane-close

    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!isEnabled) {
                flushSync(() => setOpen(false));
                return;
            }
            flushSync(() => setOpen(next));
        },
        [isEnabled]
    );

    const handleSelect = (type: "condition" | "action") => {
        if (!isEnabled) return;
        setOpen(false);
        addStep(data.insertIndex, type);
    };

    const content = (
        <div className="add-step-popover d-flex flex-column gap-1" onClick={(e) => e.stopPropagation()}>
            <div
                className="add-step-popover-option d-flex align-items-center gap-3"
                onClick={() => handleSelect("condition")}
                role="button"
                tabIndex={0}
                aria-label="Add condition step. Add a filter to check."
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelect("condition");
                    }
                }}
            >
                <div className="add-step-popover-icon d-flex align-items-center justify-content-center">
                    <div className="automationIconsSprite condition-step-icon"></div>
                </div>
                <div className="add-step-popover-text d-flex flex-column gap-1">
                    <span className="add-step-popover-label">Condition</span>
                    <span className="add-step-popover-desc">Add a filter to check</span>
                </div>
            </div>
            <div
                className="add-step-popover-option d-flex align-items-center gap-3"
                onClick={() => handleSelect("action")}
                role="button"
                tabIndex={0}
                aria-label="Add action step. Perform an action."
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelect("action");
                    }
                }}
            >
                <div className="add-step-popover-icon d-flex align-items-center justify-content-center">
                    <div className="automationIconsSprite action-step-icon"></div>
                </div>
                <div className="add-step-popover-text d-flex flex-column gap-1">
                    <span className="add-step-popover-label">Action</span>
                    <span className="add-step-popover-desc">Perform an action</span>
                </div>
            </div>
        </div>
    );

    return (
        <div
            className="add-step-node d-flex align-items-center justify-content-center"
            onClick={(e) => e.stopPropagation()}
        >
            <Handle type="target" position={Position.Top} className="add-step-node-handle" />
            <Popover
                content={content}
                trigger="click"
                open={open}
                onOpenChange={handleOpenChange}
                placement="right"
                arrow
                overlayClassName="add-step-popover-overlay"
            >
                <button
                    type="button"
                    className={`add-step-node-btn d-flex align-items-center justify-content-center nodrag nopan ${!isEnabled ? "add-step-node-btn-disabled" : ""}`}
                    aria-label="Add step. Add a condition or action."
                    disabled={!isEnabled}
                >
                    <span
                        className={`add-step-node-icon d-inline-flex align-items-center justify-content-center ${open ? "add-step-node-icon-open" : ""}`}
                    >
                        <PlusOutlined />
                    </span>
                </button>
            </Popover>
            <Handle type="source" position={Position.Bottom} className="add-step-node-handle" />
        </div>
    );
};

export const AddStepNode = React.memo(AddStepNodeComponent);
